import React, { ChangeEvent, FormEvent, Fragment } from "react";
import ReactModal from "react-modal";

import { DateTime } from "luxon";
import { V1, V2 } from "../../Api";

import "../../list.css";
import { formatDateLocal } from "../../Utilities";

interface IHealthFairEditNoticesDialogProps {
    isOpen: boolean;

    after: DateTime;
    before: DateTime;

    onRequestClose?: (event?: (React.MouseEvent | React.KeyboardEvent)) => void;
    onSuccess?: () => void;
}

interface IHealthFairEditNoticesDialogState {
    isProcessing: boolean | string;

    notices: any[];
    changes: IItemChange[];
}

interface IItemChange {
    action: "add" | "remove" | "edit",
    id?: string;
    data?: any
}

class HealthFairEditNoticesDialog extends React.Component<IHealthFairEditNoticesDialogProps, IHealthFairEditNoticesDialogState> {
    constructor(props: Readonly<IHealthFairEditNoticesDialogProps>) {
        super(props);

        const state: IHealthFairEditNoticesDialogState = {
            isProcessing: false,

            notices: [],
            changes: [],
        };

        this.state = state;

        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.handleElementChanged = this.handleElementChanged.bind(this);
        this.requestClose = this.requestClose.bind(this);
        this.addNotice = this.addNotice.bind(this);
    }

    public componentDidUpdate(prevProps: Readonly<IHealthFairEditNoticesDialogProps>) {
        if (prevProps.isOpen !== this.props.isOpen && this.props.isOpen) {
            this.loadData();
        }
    }

    public render() {
        if (!this.props.isOpen) {
            return null;
        }

        return (
            <ReactModal
                isOpen={this.props.isOpen} onRequestClose={this.requestClose}
                overlayClassName="modal-overlay" className="modal-content modal-content-600" portalClassName="modal-viewer-portal"
                bodyOpenClassName="modal-viewer-open"
                contentLabel="Edit Notices">
                <form className="input-form" onSubmit={this.handleFormSubmit}>
                    <section className="info-block">
                        <header>
                            <h3>Edit Notices</h3>
                        </header>

                        <div className="input-form-group">
                            <label>Active notices</label>
                            <ul className="list-basic list-hover">
                                {this.state.notices.map((a, i) => <li key={a.id} className="flex">
                                    {a.isEdit
                                        ? <div className="flex">
                                            <input type="text" className="" name="text" value={a.text} autoFocus={true} onChange={e => this.handleNoticeChange(e, a)} /><br />
                                            <div className="control-group mobilevertical mobileflex">
                                                <input type="date" className="lowpad" name="startDate" value={a.startDate} onChange={e => this.handleNoticeChange(e, a)} />
                                                <span> to </span>
                                                <input type="date" className="lowpad" name="endDate" value={a.endDate} onChange={e => this.handleNoticeChange(e, a)} />
                                                <button type="button" className="button btn-outline small btn-inline-center noflex" title="Save" disabled={!a.text || !a.startDate || !a.endDate} onClick={e => this.toggleEdit(a)}><i className="icon-check" /></button>
                                            </div>
                                        </div>
                                        : <Fragment>
                                            {a.text} &mdash; {formatDateLocal(a.startDate, true)} &mdash; {formatDateLocal(a.endDate, true)} <span className="spring" />
                                            <div className="control-group mobileflex">
                                                <button type="button" className="button btn-light smaller btn-inline-center flex" title="Edit" onClick={e => this.toggleEdit(a)}><i className="icon-pencil" /></button>
                                                <button type="button" className="button btn-light smaller btn-inline-center flex red" title="Remove Range" onClick={() => this.deleteNotice(i)}><i className="icon-times" /></button>
                                            </div>
                                        </Fragment>}
                                </li>)}
                            </ul>
                            &nbsp;
                            <div>
                                <button type="button" className="button text-with-icon" disabled={this.state.isProcessing === true} onClick={this.addNotice}><i className="icon-plus" /><span>New Notice</span></button>
                            </div>
                        </div>

                        {
                            this.state.isProcessing === true
                                ? <div className="spinner lds-dual-ring" />
                                : (typeof this.state.isProcessing) === "string"
                                    ? <span className="error-text">{this.state.isProcessing}</span>
                                    : ""
                        }
                        &nbsp;
                        <div className="action-panel-dialog">
                            <button type="submit" className="button push-right" disabled={this.state.isProcessing === true || this.state.notices.some(a => a.isEdit)}>Save</button>
                            <button type="button" className="button" onClick={this.requestClose}>Cancel</button>
                        </div>
                    </section>
                </form>
            </ReactModal>
        );
    }

    private handleElementChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        this.setState({
            [name]: value
        } as any);
    }

    private requestClose(e?: (React.MouseEvent | React.KeyboardEvent)) {
        if (this.state.isProcessing !== true && this.props.onRequestClose) {
            this.props.onRequestClose(e);
        }
    }

    private async loadData() {
        try {
            this.setState({
                isProcessing: true,
                notices: [],
                changes: []
            });

            const parameters = [
                `after=${this.props.after}`,
                `before=${this.props.before}`
            ];

            const notices = await V1.get<any[]>(`fairs/notices?${parameters.join("&")}`);
            notices.forEach(a => {
                a.startDate = a.startDate.substring(0, 10);
                a.endDate = a.endDate.substring(0, 10);
            });

            this.setState({
                isProcessing: false,

                notices
            });
        }
        catch (e) {
            this.setState({
                isProcessing: e.message || e || "Could not load notices"
            });
        }
    }

    private async handleFormSubmit(e: FormEvent) {
        e.preventDefault();

        try {
            this.setState({
                isProcessing: true
            });

            const changes = this.state.changes;
            for (const change of changes) {
                if (change.action === "add") {
                    delete change.id;
                    change.data.id = "";
                }
            }

            await V2.patch("fairs/notices", changes);

            this.setState({
                isProcessing: false
            }, () => {
                if (this.props.onSuccess) {
                    this.props.onSuccess();
                }

                this.requestClose();
            });
        }
        catch (e) {
            this.setState({
                isProcessing: e.message || e || "Could not delete item"
            });
        }
    }

    private handleNoticeChange(e: ChangeEvent<HTMLInputElement>, item: any) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        item[name] = value;

        this.setState(s => ({
            notices: s.notices
        }));
    }

    private toggleEdit(item: any) {
        item.isEdit = !item.isEdit;

        const changes = this.state.changes;
        if (!item.isEdit) {
            // Done making changes
            if (!this.state.changes.some(a => a.id === item.id)) {
                changes.push({
                    action: "edit",
                    id: item.id,
                    data: item
                });
            }
        }

        this.setState(s => ({
            notices: s.notices,
            changes
        }));
    }

    private deleteNotice(index: number) {
        const { notices, changes } = this.state;

        const [removed] = notices.splice(index, 1);
        const existingChange = changes.find(a => a.id === removed.id);
        if (existingChange && existingChange.action === "add") {
            // Deleting a new notice, just remove from change list
            changes.splice(changes.indexOf(existingChange), 1);
        }
        else if (existingChange && existingChange.action !== "remove") {
            // Deleting a notice we edited, just change to remove
            existingChange.action = "remove";
        }
        else {
            // Deleting an existing notice that we didn't change
            changes.push({
                action: "remove",
                id: removed.id
            });
        }

        this.setState({
            notices,
            changes
        });
    }

    private addNotice() {
        const { notices, changes } = this.state;

        notices.push({
            isEdit: true,
            id: "new" + Date.now(),
            text: "",
            startDate: "",
            endDate: ""
        });

        changes.push({
            action: "add",
            id: notices[notices.length - 1].id,
            data: notices[notices.length - 1]
        });

        this.setState({
            notices,
            changes
        });
    }
}

export default HealthFairEditNoticesDialog;
